import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { SkeletonBox } from '../../../SkeletonBox/SkeletonBox.es.js';
import { InputContext } from '../../Input.Context/Input.Context.es.js';
import { InputInlineAffix } from '../../Input.Inline/Input.Inline.Affix/Input.Inline.Affix.es.js';
import '../../Input.Inline/Input.Inline.Icon/Input.Inline.Icon.es.js';
import '../../Input.Inline/Input.Inline.IconButton/Input.Inline.IconButton.es.js';
import { getMask } from './mask/index.es.js';

/**
 * SetSelectionRange method apply only to inputs of types text, search, URL, tel and password.
 *
 * Https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange.
 */ const validSelectionRangeTypes = [
    'text',
    'search',
    'URL',
    'tel',
    'password'
];
const InputFieldText = /*#__PURE__*/ forwardRef(({ className, children, onBlur, onFocus, onChange, onContainerClick, overlay, manageCursor = false, type = 'text', value, obscureOnBlur, mask, ...rest }, // Ref provided by react hook form or downshift
ref)=>{
    const { id, inputLabelId, inputMessageId, loading, warning, disabled, placeholder, readOnly, error, display, labelProps, label } = useContext(InputContext);
    const [cursor, setCursor] = useState(null);
    const inputRef = useRef(null);
    const [inFocus, setInFocus] = useState(false);
    const masker = useMemo(()=>{
        if (mask != null) {
            return getMask(mask);
        }
    }, [
        mask
    ]);
    const handleChange = useCallback((ev)=>{
        if (masker) {
            masker.resolve(ev.target.value);
            // compare the formatted value with the current
            // value to avoid unecessary onChange calls
            if (masker.value === value) {
                return;
            }
            ev.target.value = masker.value;
        }
        // Value is updated asynchronously,
        // store cursor to prevent jumping
        setCursor(ev.target.selectionStart);
        onChange?.(ev);
    }, [
        masker,
        onChange,
        value
    ]);
    const handleFocus = useCallback((ev)=>{
        setInFocus(true);
        onFocus?.(ev);
    }, [
        onFocus
    ]);
    const handleBlur = useCallback((ev)=>{
        const trimmed = ev.target.value.trim(); // Trim value
        const changed = ev.target.value !== trimmed; // Change check
        // to avoid triggering unnecessary change event
        // trim when the value truly changed
        if (changed && type === 'text') {
            onChange?.(ev);
        }
        setInFocus(false);
        onBlur?.(ev);
    }, [
        onBlur,
        onChange,
        type
    ]);
    const handleMouseUp = useCallback((ev)=>{
        setCursor(ev.currentTarget.selectionStart);
    }, [
        setCursor
    ]);
    useEffect(()=>{
        const input = inputRef.current;
        if (validSelectionRangeTypes.includes(input?.type ?? '') && manageCursor) {
            input?.setSelectionRange?.(cursor, cursor);
        }
    }, [
        cursor,
        value,
        manageCursor
    ]);
    const handleOnContainerClick = useCallback((event)=>{
        const input = inputRef.current;
        if (!disabled && input != null) {
            // User clicked outside of input element
            if (event.target != null && !event.target.classList.contains('input')) {
                if (validSelectionRangeTypes.includes(input?.type ?? '')) {
                    // Place cursor at beginning of input
                    input?.setSelectionRange(0, 0);
                }
                input?.focus();
            }
            onContainerClick?.(event);
        }
    }, [
        disabled,
        onContainerClick
    ]);
    const setRef = useCallback((e)=>{
        // Define ref separate from downshift/react-hook-form
        if (e != null) {
            inputRef.current = e;
        }
        if (typeof ref === 'function') {
            ref(e);
        } else if (ref !== null) {
            ref.current = e;
        }
    }, [
        ref
    ]);
    const fieldTextCss = classNames('field-container', 'field-text', {
        'has-value': Boolean(value),
        error,
        disabled,
        warning
    });
    /**
     * Formats the value of the field if not already formatted.
     */ const displayValue = useMemo(()=>{
        if (masker == null || value == null) return value?.toString();
        masker.resolve(value.toString());
        return masker.value;
    }, [
        masker,
        value
    ]);
    const inputType = useMemo(()=>obscureOnBlur && !inFocus ? 'password' : type, [
        type,
        inFocus,
        obscureOnBlur
    ]);
    return jsxs("div", {
        className: fieldTextCss,
        "data-testid": "input-field-text",
        onClick: handleOnContainerClick,
        children: [
            loading && jsx(SkeletonBox, {}),
            jsxs("div", {
                className: "field-content",
                children: [
                    jsx("input", {
                        ref: setRef,
                        "aria-describedby": inputMessageId,
                        "aria-labelledby": inputLabelId,
                        className: "input",
                        "data-testid": "input",
                        disabled: disabled,
                        id: id,
                        onBlur: handleBlur,
                        onChange: handleChange,
                        onFocus: handleFocus,
                        onMouseUp: handleMouseUp,
                        placeholder: placeholder,
                        readOnly: readOnly,
                        type: inputType,
                        value: displayValue,
                        ...rest
                    }),
                    overlay && jsx("div", {
                        className: "field-display-value",
                        children: displayValue
                    })
                ]
            }),
            label && display === 'inline' && jsx(InputInlineAffix, {
                intent: "label",
                labelProps: labelProps,
                variant: "prefix",
                children: label
            }),
            children
        ]
    });
});
InputFieldText.displayName = 'Input.Field.Text';

export { InputFieldText, InputFieldText as default };
//# sourceMappingURL=Input.Field.Text.es.js.map
